/**
 * Utility to parse WhatsApp "Online Payment" order messages.
 * Handles Unicode formatting (mathematical bold/italic), custom emojis,
 * multi-line addresses, flexible key delimiters, and standardizes
 * phone numbers, pin codes, and state names.
 */

export const INDIAN_STATES = {
  tamilnadu: "Tamil Nadu",
  "tamil nadu": "Tamil Nadu",
  kerala: "Kerala",
  karnataka: "Karnataka",
  andhrapradesh: "Andhra Pradesh",
  "andhra pradesh": "Andhra Pradesh",
  telangana: "Telangana",
  maharashtra: "Maharashtra",
  delhi: "Delhi",
  gujarat: "Gujarat",
  rajasthan: "Rajasthan",
  uttarpradesh: "Uttar Pradesh",
  "uttar pradesh": "Uttar Pradesh",
  westbengal: "West Bengal",
  "west bengal": "West Bengal",
  punjab: "Punjab",
  haryana: "Haryana",
  bihar: "Bihar",
  madhyapradesh: "Madhya Pradesh",
  "madhya pradesh": "Madhya Pradesh",
  odisha: "Odisha",
  orissa: "Odisha",
  assam: "Assam",
  goa: "Goa",
  jharkhand: "Jharkhand",
  chhattisgarh: "Chhattisgarh",
  uttarakhand: "Uttarakhand",
  himachalpradesh: "Himachal Pradesh",
  "himachal pradesh": "Himachal Pradesh",
  tripura: "Tripura",
  meghalaya: "Meghalaya",
  manipur: "Manipur",
  nagaland: "Nagaland",
  mizoram: "Mizoram",
  sikkim: "Sikkim",
  arunachalpradesh: "Arunachal Pradesh",
  "arunachal pradesh": "Arunachal Pradesh",
  pondicherry: "Puducherry",
  puducherry: "Puducherry",
  chandigarh: "Chandigarh",
  jammuandkashmir: "Jammu and Kashmir",
  "jammu and kashmir": "Jammu and Kashmir",
  ladakh: "Ladakh",
};

export const formatTitleCase = (str) => {
  if (!str) return "";
  return str
    .trim()
    .split(/\s+/)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export const normalizeIndianState = (val) => {
  if (!val) return "";
  const cleaned = val.trim();
  const key = cleaned.toLowerCase().replace(/[\s_-]+/g, "");
  for (const [k, v] of Object.entries(INDIAN_STATES)) {
    if (k.replace(/[\s_-]+/g, "") === key) {
      return v;
    }
  }
  return formatTitleCase(cleaned);
};

export const cleanPhoneNumber = (val) => {
  if (!val) return "";
  const digits = String(val).replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) {
    return digits.slice(2);
  }
  if (digits.length === 11 && digits.startsWith("0")) {
    return digits.slice(1);
  }
  if (digits.length >= 10) {
    return digits.slice(-10);
  }
  return digits;
};

export const parseOnlinePaymentMessage = (text) => {
  if (!text || typeof text !== "string") return null;

  // 1. Normalize Unicode mathematical alphanumeric & special formatting
  const normalized = text.normalize("NFKC");
  const lines = normalized.split(/\r?\n/);

  const keyMap = [
    { key: "name", regex: /^(?:name|customer\s*name|cust\s*name|full\s*name|buyer\s*name)$/i },
    { key: "address", regex: /^(?:address|address\s*line\s*1|addr|door\s*no|house\s*no|flat\s*no|street)$/i },
    { key: "place", regex: /^(?:place|address\s*line\s*2|locality|area|town|landmark)$/i },
    { key: "postoffice_name", regex: /^(?:post\s*office(?:\s*name)?|postoffice(?:\s*name)?|p\.?o\.?(?:\s*name)?)$/i },
    { key: "city", regex: /^(?:district|city|dist)$/i },
    { key: "state", regex: /^(?:state|province)$/i },
    { key: "zip", regex: /^(?:pincode|pin\s*code|pin|zip|zip\s*code|postal\s*code)$/i },
    { key: "whatsapp", regex: /^(?:whats\s*app\s*no|whatsapp\s*no|whats\s*app\s*number|whatsapp\s*number|whats\s*app|whatsapp|wa\s*no|wp\s*no|wa\s*number|wp\s*number)$/i },
    { key: "phone", regex: /^(?:mobile\s*no|mobile\s*number|mobile|phone\s*no|phone\s*number|phone|contact\s*no|contact\s*number|contact|ph\s*no|ph|alt\s*no|alternative\s*no)$/i },
    { key: "size", regex: /^(?:size|sizes|dress\s*size)$/i },
    { key: "product_name", regex: /^(?:product\s*name|product|item\s*name|item|dress|model)$/i },
    { key: "amount", regex: /^(?:amount|price|total\s*amount|total|rate|paid|paid\s*amount)$/i },
  ];

  // Regex to detect formatting headers, footers, copy-paste notices, and horizontal dividers
  const ignoreRegex = /^(?:order\s*format|write\s*your|copy\s*paste|delivery\s*details|customer\s*details|fill\s*that|thank\s*you|[-_=*~]{3,}|[👇🏻👆🏻👇👆])/i;

  const rawExtracted = {};
  let currentKey = null;

  for (let rawLine of lines) {
    const line = rawLine.trim();
    if (!line) continue;

    // Check if line should be ignored as instructions/divider
    if (ignoreRegex.test(line)) {
      currentKey = null;
      continue;
    }

    // Strip markdown formatting symbols (*, _, ~, `)
    const cleanedLine = line.replace(/[*_~`]/g, "").trim();

    // Match "Key :: Value", "Key : Value", "Key = Value", or "Key - Value"
    // Also tolerate leading emojis or symbols before the key
    const match = cleanedLine.match(/^(?:[^\w\s]*\s*)?([A-Za-z0-9_#\s./()&-]+?)\s*(?:::|:|=|\s-\s)\s*(.*)$/);

    if (match) {
      const rawKey = match[1].trim();
      const rawVal = match[2].trim();

      const found = keyMap.find((k) => k.regex.test(rawKey));
      if (found) {
        currentKey = found.key;
        rawExtracted[currentKey] = rawVal;
        continue;
      }
    }

    // If continuation line for address or place
    if (currentKey && (currentKey === "address" || currentKey === "place")) {
      if (!ignoreRegex.test(line) && !line.startsWith("(") && !line.endsWith(")")) {
        rawExtracted[currentKey] = rawExtracted[currentKey]
          ? `${rawExtracted[currentKey]} ${cleanedLine}`
          : cleanedLine;
      }
    }
  }

  // Format and clean up extracted fields
  const result = {};
  let fieldsCount = 0;

  if (rawExtracted.name) {
    result.name = formatTitleCase(rawExtracted.name);
    fieldsCount++;
  }
  if (rawExtracted.address) {
    result.address = rawExtracted.address.replace(/[,.\s]+$/, "").trim();
    fieldsCount++;
  }
  if (rawExtracted.place) {
    result.place = rawExtracted.place.replace(/[,.\s]+$/, "").trim();
    fieldsCount++;
  }
  if (rawExtracted.postoffice_name) {
    result.postoffice_name = formatTitleCase(
      rawExtracted.postoffice_name.replace(/[,.\s]+$/, "").trim()
    );
    fieldsCount++;
  }
  if (rawExtracted.city) {
    result.city = formatTitleCase(
      rawExtracted.city.replace(/[,.\s]+$/, "").trim()
    );
    fieldsCount++;
  }
  if (rawExtracted.state) {
    result.state = normalizeIndianState(rawExtracted.state);
    fieldsCount++;
  }
  if (rawExtracted.zip) {
    const cleanZip = rawExtracted.zip.replace(/[^\d]/g, "").slice(0, 6);
    if (cleanZip) {
      result.zip = cleanZip;
      fieldsCount++;
    }
  }
  if (rawExtracted.phone) {
    const cleanPh = cleanPhoneNumber(rawExtracted.phone);
    if (cleanPh) {
      result.phone = cleanPh;
      fieldsCount++;
    }
  }
  if (rawExtracted.whatsapp) {
    const cleanWp = cleanPhoneNumber(rawExtracted.whatsapp);
    if (cleanWp) {
      result.whatsapp = cleanWp;
      fieldsCount++;
    }
  }
  if (rawExtracted.size) {
    result.size = rawExtracted.size.trim();
    fieldsCount++;
  }
  if (rawExtracted.product_name) {
    result.product_name = rawExtracted.product_name.trim();
    fieldsCount++;
  }
  if (rawExtracted.amount) {
    const cleanAmt = rawExtracted.amount.replace(/[^\d.]/g, "");
    if (cleanAmt) {
      result.amount = cleanAmt;
      fieldsCount++;
    }
  }

  // Sync phone and whatsapp if only one was provided
  if (result.phone && !result.whatsapp) {
    result.whatsapp = result.phone;
  } else if (result.whatsapp && !result.phone) {
    result.phone = result.whatsapp;
  }

  return {
    data: result,
    count: fieldsCount,
  };
};
